import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { api, formatCurrency } from '../lib/api';
import { APP_NAME } from '../lib/appConfig';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { 
  LayoutDashboard, Users, MessageCircle, Wallet,
  LogOut, Settings, User, Menu, X, Bell, Gift,
  CheckCheck, Trash2, Loader2, Shield
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import LanguageSelector from './LanguageSelector';
import PWAInstallButton from './PWAInstallButton';
import useNotificationsRealtime from '../hooks/useNotificationsRealtime';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    setNotifications,
    setUnreadCount,
    refreshNotifications,
  } = useNotificationsRealtime({ enabled: !!user, fastMode: notifOpen });

  // Dynamic nav items with translations
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkAdmin = async () => {
      try {
        const res = await api.getAdminStats();
        if (!cancelled) {
          setIsAdmin((res.data?.total_tontines || 0) > 0);
        }
      } catch (e) {
        // On error, do NOT assume admin; keep hidden
        if (!cancelled) setIsAdmin(false);
      }
    };
    if (user) checkAdmin();
    return () => { cancelled = true; };
  }, [user]);

  const navItems = [
    { href: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
    { href: '/tontines', labelKey: 'nav.tontines', icon: Users },
    { href: '/contributions', labelKey: 'nav.contributions', icon: Wallet },
    { href: '/referrals', labelKey: 'nav.referrals', icon: Gift },
    { href: '/chat', labelKey: 'nav.chat', icon: MessageCircle },
    // Admin is conditionally shown based on actual admin status
    ...(isAdmin ? [{ href: '/admin', labelKey: 'nav.admin', icon: Shield }] : []),
  ];

  useEffect(() => {
    if (notifOpen) {
      refreshNotifications({ force: true });
    }
  }, [notifOpen, refreshNotifications]);

  const handleMarkAllRead = async () => {
    setNotifLoading(true);
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking notifications read:', error);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await api.deleteNotification(notificationId);
      const deleted = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    handleMarkRead(notification.id);
    if (notification.tontine_id) {
      navigate(`/tontines/${notification.tontine_id}`);
      setNotifOpen(false);
    }
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';
  };

  return (
    <nav className="sticky top-0 z-50 glass dark:glass-dark border-b border-stone-200/50 dark:border-stone-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
            <div className="w-9 h-9 bg-gradient-to-br from-stone-900 to-stone-700 dark:from-stone-100 dark:to-stone-300 rounded-xl flex items-center justify-center">
              <span className="text-white dark:text-stone-900 font-display font-bold text-base">T</span>
            </div>
            <span className="font-display font-bold text-lg text-stone-900 dark:text-stone-50 hidden sm:block whitespace-nowrap max-w-[180px] truncate leading-none">
              {APP_NAME}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
              const label = t(item.labelKey);
              return (
                <Link key={item.href} to={item.href} data-testid={`nav-${item.labelKey.split('.')[1]}`}>
                  <Button
                    variant={isActive ? 'secondary' : 'ghost'}
                    className={`gap-2 whitespace-nowrap ${isActive ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm ring-1 ring-primary/20 font-semibold' : ''}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {label}
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* PWA Install Button */}
            <PWAInstallButton variant="navbar" />
            
            {/* Language Selector */}
            <LanguageSelector />
            
            {/* Notifications */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-orange-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="font-semibold text-stone-900 dark:text-stone-50">{t('notifications.title')}</h3>
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-orange-600 hover:text-orange-700"
                      onClick={handleMarkAllRead}
                      disabled={notifLoading}
                    >
                      {notifLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <CheckCheck className="w-3 h-3 mr-1" />
                      )}
                      {t('notifications.markAllRead')}
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[320px]">
                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-stone-500">
                      <Bell className="w-10 h-10 mb-2 opacity-30" />
                      <p className="text-sm">{t('notifications.noNotifications')}</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stone-100 dark:divide-stone-800">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/50 cursor-pointer transition-colors ${
                            !notification.read ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                          data-testid={`notification-${notification.id}`}
                        >
                          <div className="flex gap-3">
                            <span className="text-xl flex-shrink-0">{notification.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={`text-sm ${!notification.read ? 'font-medium' : ''} text-stone-900 dark:text-stone-100`}>
                                  {notification.title}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteNotification(notification.id);
                                  }}
                                  className="text-stone-400 hover:text-red-500 transition-colors p-1 -mr-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <p className="text-xs text-stone-600 dark:text-stone-400 mt-0.5 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-[10px] text-stone-400 mt-1">
                                {formatNotificationTime(notification.created_at)}
                              </p>
                            </div>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                <div className="border-t px-3 py-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-center"
                    onClick={() => {
                      navigate('/notifications');
                      setNotifOpen(false);
                    }}
                    data-testid="notifications-view-all-btn"
                  >
                    {t('notifications.viewAll')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-2" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.avatar_url || undefined} alt={user?.full_name || 'User avatar'} />
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-sm font-medium">
                      {getInitials(user?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">
                    {user?.full_name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-stone-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" />
                  {t('nav.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" />
                  {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 pb-4">
          <div className="px-4 pt-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
              const label = t(item.labelKey);
              return (
                <Link 
                  key={item.href} 
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button
                    variant={isActive ? 'secondary' : 'ghost'}
                    className={`w-full justify-start gap-3 ${isActive ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm ring-1 ring-primary/20 font-semibold' : ''}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {label}
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
